import { useReactiveVar } from '@apollo/client'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { useNodeMapShallowSelector } from 'components/WorkspaceEditor/features/keyOperations/store/NodeMapStore'
import { useSelectedMainMenuNode } from 'components/WorkspaceEditor/HoveredItemStore'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import { getSortedSiblings } from 'components/WorkspaceEditor/utils'
import { useContentItemViewNodes } from 'components/WorkspaceEditor/Workspace2Store'
import { useMemo } from 'react'

/**
 * A custom hook that determines whether the "Move Up" and "Move Down" actions
 */
export const useMoveActionAvailability = () => {
    const contentItemViewNodes = useContentItemViewNodes()
    const selectedMainMenuNode = useSelectedMainMenuNode()
    const { selectedElementNode: selectedProjectElementNode } = useReactiveVar(
        workspaceSelectedElement
    )
    const { isSaveOperationInProgress } = useNodeMapShallowSelector(
        (state) => ({
            isSaveOperationInProgress: state.isSaveOperationInProgress,
        })
    )

    /**
     * Gets a list of sibling nodes, sorted according to workspace logic.
     */
    const siblingNodesSortedByOrder: Partial<
        ContentItemViewNodesType
    >[] = useMemo(() => {
        return getSortedSiblings(
            contentItemViewNodes,
            selectedMainMenuNode?.dtvParentId,
            selectedMainMenuNode?.dtvContentItemParentId
        )
    }, [contentItemViewNodes, selectedMainMenuNode])

    /**
     * Finds the index of the currently selected node among its siblings.
     */
    const selectedNodeSiblingIndex: number = useMemo(() => {
        if (!selectedMainMenuNode) return -1
        return siblingNodesSortedByOrder.findIndex(
            (item) => item.id === selectedMainMenuNode?.id
        )
    }, [siblingNodesSortedByOrder, selectedMainMenuNode?.id])
    const canMoveUp = useMemo(
        () =>
            !selectedProjectElementNode?.isInProject ||
            selectedNodeSiblingIndex <= 0 ||
            isSaveOperationInProgress,
        [
            selectedProjectElementNode?.isInProject,
            selectedNodeSiblingIndex,
            isSaveOperationInProgress,
        ]
    )

    const canMoveDown = useMemo(
        () =>
            !selectedProjectElementNode?.isInProject ||
            selectedNodeSiblingIndex === siblingNodesSortedByOrder.length - 1 ||
            selectedNodeSiblingIndex === -1 ||
            isSaveOperationInProgress,
        [
            selectedProjectElementNode?.isInProject,
            selectedNodeSiblingIndex,
            siblingNodesSortedByOrder.length,
            isSaveOperationInProgress,
        ]
    )

    return {
        canMoveUp,
        canMoveDown,
    }
}
